import { useMemo } from 'react';
import { Box, Grid } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useLandUseRedesignationV4 } from 'hooks/useDocuments';
import { useLandUseRedesignation } from 'hooks/useLandUseRedesignation';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { PDFMaterial } from 'types/Documents';
import { trackProjectMaterials } from 'utils/analytics';
import { City } from 'utils/constants/location_legacy';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { isCityMatch } from 'utils/misc';

const descriptionStyle = {
  textTransform: 'capitalize',
};

const ellipsisContainer = {
  maxWidth: '400px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
};

const pdfMaterialContainer = {
  maxWidth: '100%',
  gap: 2,
};

const ProposedInfoSection = ({
  label,
  lud,
}: {
  label: string;
  lud: String | undefined;
}) => {
  return (
    <Box>
      <Label>{label}</Label>
      {lud && typeof lud === 'string' ? (
        <Text sx={descriptionStyle}>{lud}</Text>
      ) : (
        <EmptyField />
      )}
    </Box>
  );
};

export const LandUseRedesignation = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result: any;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useLandUseRedesignationV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('landUseRedesignation');
    resolvedDocumentId = documentUuid || undefined;
    result = useLandUseRedesignation(documentUuid);
  }

  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const currentCity = result.data?.city;
  const isDallas = isCityMatch(currentCity, City.DALLAS);

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result.data?.metadata?.relatedcontacts || []),
    [result.data?.metadata],
  );

  const wordBreak = { wordBreak: 'break-all' };
  const { data } = result;

  const permitUrl = data?.document_url;
  const showWebApplication = Boolean(permitUrl);

  const hasMaterials =
    data?.metadata?.pdf_material_urls && data?.metadata?.pdf_material_urls.length > 0;
  const hasRelevantMaterials = showWebApplication || hasMaterials;

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Rezoning">
          {result.data?.metadata.permitnum}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={data?.metadata?.parcel_address ?? ''}
          city={data?.city ?? ''}
          state_code={data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={data?.companies}
          applicant={data?.metadata.applicant}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />
        <InfoModal.Row>
          <Label>Project Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.projectname ? (
                <Text sx={{ fontWeight: 'bold' }}>
                  {result.data.metadata.projectname}
                </Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>
        <InfoModal.Row hideDivider>
          <Label>Description</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {data?.metadata.description ? (
                <Text sx={descriptionStyle}>{data?.metadata.description}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>
        {!isDallas && (
          <InfoModal.Row hideDivider>
            <Label>Proposed</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={8}>
                <Grid templateColumns="1fr" gridGap={3} alignItems="flex-start">
                  <ProposedInfoSection label="From" lud={data?.metadata.fromlud} />
                  <ProposedInfoSection label="To" lud={data?.metadata.proposedlud} />
                </Grid>
              </Skeleton>
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
        <ModalStatus activities={data?.activities} Skeleton={Skeleton} />
        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
        {!result.isLoading && hasRelevantMaterials && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <FlexColumn sx={pdfMaterialContainer}>
                {showWebApplication && permitUrl && (
                  <ExternalLink
                    to={permitUrl}
                    onClick={() => trackProjectMaterials(permitUrl)}
                  >
                    View application
                  </ExternalLink>
                )}
                {data?.metadata.pdf_material_urls?.map((material: PDFMaterial) => (
                  <ExternalLink
                    key={`pdf-material-${material.document_url}`}
                    to={material.document_url}
                    onClick={() => trackProjectMaterials(material.document_url)}
                    sx={wordBreak}
                  >
                    <Box sx={ellipsisContainer}>{material.document_url}</Box>
                  </ExternalLink>
                ))}
              </FlexColumn>
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
        {!result.isLoading && !hasRelevantMaterials && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <EmptyField />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};
